<script setup>
    import { ref, onMounted, watch, computed } from "vue"
    import managerRouter from "~/router/manager.router"
    import { drawerMenu } from "~/logic/route.logic"
    import { updateFavicon } from "~/logic/dom.logic"
    import {
        useDataCacheStore,
        useEntrepriseStore,
        useAppStore,
        useAuthStore,
        usePersonnelStore,
    } from "~/stores"
    import { useTheme } from "vuetify"

    import { useRoute } from "vue-router"

    const router = useRoute()

    const dataCacheStore = useDataCacheStore()
    const entrepriseStore = useEntrepriseStore()
    const appStore = useAppStore()
    const authStore = useAuthStore()
    const personnelStore = usePersonnelStore()

    const appReady = ref(true)
    const errors = ref(null)
    const drawer = ref(false)

    const theme = useTheme()

    function setCompany(company) {
        const lightTheme = theme.themes.value.light
        lightTheme.colors.primary = company.COLOR
        updateFavicon(company.LOGO)
    }

    // Computed pour avoir les rôles de l'utilisateur de manière réactive
    const userRoles = computed(() => authStore.getUser?.ROLES)

    // Mettre à jour le menu à chaque fois que les rôles changent
    function updateMenu() {
        if (userRoles.value) {
            appStore.drawerMenu = drawerMenu(
                managerRouter,
                "manager",
                userRoles.value,
            )
        }
    }

    // Watcher pour recalculer le menu quand les rôles changent
    watch(
        userRoles,
        () => {
            updateMenu()
        },
        { deep: true },
    )

    onMounted(async () => {
        setCompany(entrepriseStore.currentItem)

        await personnelStore.ready
        updateMenu()

        await dataCacheStore.fetchDataCache()
        setCompany(entrepriseStore.currentItem)
        // Le menu sera automatiquement mis à jour via le watcher si les rôles ont changé
    })
</script>

<template>
    <AppFullLoader v-if="!appReady" :errors="errors" />
    <div v-else>
        <v-navigation-drawer v-model="drawer" density="compact">
            <v-list>
                <template
                    v-for="(item, i) in appStore.drawerMenu"
                    :key="`menu-${i}`"
                >
                    <v-list-item
                        :prepend-icon="item.icon"
                        :title="item.text"
                        :to="{ name: item.to }"
                    />
                    <v-divider />
                </template>
            </v-list>
        </v-navigation-drawer>

        <v-app-bar>
            <v-app-bar-nav-icon @click.stop="drawer = !drawer" />
            <v-spacer />
            <v-app-bar-title>
                <AppLogo max-width="80" />
            </v-app-bar-title>
            <v-spacer />
            <template v-slot:append>
                <TheAccountMenu />
            </template>

            <v-progress-linear
                v-if="appStore.showLoading"
                absolute
                bottom
                height="5"
                indeterminate
            />
        </v-app-bar>

        <v-main>
            <router-view :key="router.fullPath" />
        </v-main>
    </div>
</template>
